import React, { useEffect, useRef, useState } from "react";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
} from "@material-ui/core";

import { copyObject } from "../../services";
import { COLORS } from "../../theme/colors";
import { ContextMenu } from "../../components/contextMenu";
import trashIcon from "../../assets/images/icons/trash.svg";
import { COUNTRIES } from "../../components/portfolioTable/constants";
import { Select, TextButton, TransparentTextInput } from "../../components/base";

const useStyles = makeStyles((theme) => ({
  table: {
    maxHeight: (props) => props.maxHeight || "60vh",
    marginTop: theme.spacing(1.75),
    borderRight: `1px solid ${COLORS.darks[0][10]}`,
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(33, 33, 33, 0.04)",
    },
    "&:focus": {
      backgroundColor: COLORS.black,
      opacity: 0.1,
    },
  },
  trashIcon: {
    position: "absolute",
    display: "none",
    cursor: "pointer",
    width: 14,
  },
  addRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  subHeadersRow: {
    position: "sticky",
    top: (props) => props.mainHeaderRowHeight,
    zIndex: 1,
    verticalAlign: "top",
  },
  subHeaderCell: {
    borderTop: "none",
  },
  mainHeaderCell: {
    borderBottom: "none",
  },
  headerRow: {
    verticalAlign: "top",
    "& th": {
      paddingTop: theme.spacing(1),
      paddingBottom: 0,
    },
  },
  stickyCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    borderRight: `1px solid ${COLORS.darks[0][10]}`,
  },
  addressAlertIcon: {
    fontSize: 20,
    color: "#ECB476",
  },
}));

export const AREA_TYPES = ["SQFT", "SQM"];

export const BUILDING_SEGMENTS = [
  "OFFICE",
  "RESIDENTIAL",
  "LOGISTIC",
  "RETAIL",
  "HOSPITALITY",
  "DATA-CENTERS",
];

export const CURRENCY_OPTIONS = ["USD", "EUR", "GBP"];

const HEADERS = [
  {},
  { key: "name", text: "Building name", minWidth: 160 },
  { key: "address", text: "Building address", minWidth: 300 },
  { key: "city", text: "City", minWidth: 150 },
  { key: "country", text: "Country", minWidth: 100 },
  { key: "zip_code", text: "Zip", minWidth: 100 },
  { key: "total_square", text: "Total Building Area", minWidth: 150, type: "number" },
  { key: "roof_square", text: "Roof Area", minWidth: 100, type: "number" },
  { key: "area_type", text: "SQM/SQF" },
  { key: "segment", text: "Segment" },
  { key: "total_unit", text: "Number of Units", minWidth: 100, type: "number" },
  { key: "total_tenants", text: "Number of Tenants", minWidth: 100, type: "number" },
  {
    key: "electricity_costs",
    text: "Total Electricity Costs (USD/EUR/GBP)",
    minWidth: 400,
    subHeaders: [
      { key: "2021", text: "2021", type: "number", minWidth: 100 },
      { key: "2022", text: "2022", type: "number", minWidth: 100 },
      { key: "2023", text: "2023 - Est.", type: "number", minWidth: 100 },
      { key: "currency", text: "Currency", type: "select", options: CURRENCY_OPTIONS },
    ],
  },
  {
    key: "electricity_consumption",
    text: "Estimated Electricity consumption (kWh)",
    minWidth: 300,
    subHeaders: [
      { key: "2021", text: "2021", type: "number", minWidth: 100 },
      { key: "2022", text: "2022", type: "number", minWidth: 100 },
      { key: "2023", text: "2023 - Est.", type: "number", minWidth: 100 },
    ],
  },
];

export const DEFAULT_ROW = {};

export const ColumnsData = ({ row, rowIndex, onCellDataChange, headers }) => {
  const classes = useStyles();

  return (
    <>
      {headers.map((header, index) => (
        <TableCell
          key={index}
          className={header.fixed && classes.stickyCell}
          style={
            (header.fixed || header.noEdit) && {
              backgroundColor:
                header.noEdit && row.is_valid_address ? COLORS.lightGreen : COLORS.white,
            }
          }
        >
          {header.noEdit ? (
            <Typography align="center">{row.is_valid_address ? row[header.key] : "N/A"}</Typography>
          ) : (
            <TransparentTextInput
              type={header.type}
              value={row[header.key]}
              onChange={(v) => onCellDataChange(rowIndex, header.key, v)}
            />
          )}
        </TableCell>
      ))}
    </>
  );
};

export const SubColumnsData = ({ row, rowIndex, onCellDataChange, headers }) => {
  return (
    <>
      {headers.map((header, j) => (
        <React.Fragment key={j}>
          {header.subHeaders.map((subHeader, i) => (
            <TableCell key={i}>
              {subHeader.type === "number" ? (
                <TransparentTextInput
                  key={i}
                  type={subHeader.type}
                  value={row[header.key]?.[subHeader.key]}
                  onChange={(v) => onCellDataChange(rowIndex, header.key, v, subHeader.key)}
                />
              ) : (
                <Select
                  options={subHeader.options}
                  value={row[header.key]?.[subHeader.key] || ""}
                  onChange={(v) => onCellDataChange(rowIndex, header.key, v, subHeader.key)}
                  displayEmpty={true}
                  placeholder="Select"
                />
              )}
            </TableCell>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

export const PortfolioTable = ({ rows, setRows, disableTable, deleteBuilding, maxHeight }) => {
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [clickedRow, setClickedRow] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);
  const [sortColumns, setSortColumns] = useState({});
  const mainHeaderRowRef = useRef();
  const newRowRef = useRef();
  const classes = useStyles({
    mainHeaderRowHeight: mainHeaderRowRef?.current?.clientHeight || 47,
    maxHeight,
  });

  useEffect(() => {
    if (!newRowRef.current) return;
    newRowRef.current.scrollIntoView();
  }, [newRowRef.current, rows.length]);

  const onCellDataChange = (rowIndex, key, value, subKey = null) => {
    if (typeof value === "string") {
      value = value.trimStart();
      if (!isNaN(value)) {
        value = parseFloat(value);
      }
    }
    setRows((prev) => {
      const newRows = copyObject(prev);
      if (subKey) {
        newRows[rowIndex][key] = newRows[rowIndex][key] || {};
        newRows[rowIndex][key][subKey] = value;
      } else {
        newRows[rowIndex][key] = value;
      }
      return newRows;
    });
  };

  const addRowAbove = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow && copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const addRowBelow = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex + 1);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex + 1)));
    if (copiedRow && copiedRow > rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const deleteRow = (rowIndex) => {
    setRows(rows.slice(0, rowIndex).concat(rows.slice(rowIndex + 1)));
    if (copiedRow > rowIndex) {
      setCopiedRow(copiedRow - 1);
    } else if (copiedRow === rowIndex) {
      setCopiedRow(null);
    }
  };

  const copyRow = (rowIndex) => {
    setCopiedRow(rowIndex);
  };

  const pasteRow = (rowIndex) => {
    if (copiedRow === null) return;
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(rows[copiedRow]);
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const getValueForSorting = (row, name, parentName = null) => {
    let v = parentName ? row[parentName]?.[name] : row[name];
    if (typeof v === "string") {
      v = v.toLowerCase();
    }
    return v;
  };

  const sortByColumn = (name, parentName = null) => {
    const columns = {};
    if (!sortColumns[name]) {
      columns[name] = "asc";
    } else if (sortColumns[name] === "asc") {
      columns[name] = "desc";
    } else {
      columns[name] = "asc";
    }
    setSortColumns(columns);
    setRows((r) => {
      const emptyRows = [];
      const rows = [];
      r.forEach((obj) => {
        if (!!getValueForSorting(obj, name, parentName)) {
          rows.push(obj);
        } else {
          emptyRows.push(obj);
        }
      });
      rows.sort((a, b) => {
        const aValue = getValueForSorting(a, name, parentName);
        const bValue = getValueForSorting(b, name, parentName);
        if (aValue > bValue) {
          return 1;
        } else if (aValue < bValue) {
          return -1;
        }
        return 0;
      });
      if (columns[name] === "asc") return rows.concat(emptyRows);
      return rows.reverse().concat(emptyRows);
    });
  };

  const addNewRow = () => {
    setRows((r) => {
      const newRows = copyObject(r);
      newRows.push(copyObject(DEFAULT_ROW));
      return newRows;
    });
  };

  const contextMenuItems = [
    { text: "Add row above", onClick: addRowAbove },
    { text: "Add row below", onClick: addRowBelow },
    { text: "Delete row", onClick: deleteRow },
    { text: "Copy", onClick: copyRow },
    { text: "Paste", onClick: pasteRow },
  ];

  const getSegment = (segment) => {
    return segment?.toUpperCase();
  };

  return (
    <>
      <TableContainer className={classes.table}>
        <Table stickyHeader style={{ height: "100%" }}>
          <TableHead>
            <TableRow className={classes.headerRow} innerRef={mainHeaderRowRef}>
              {HEADERS.map((header, index) => (
                <TableCell
                  className={!header.subHeaders?.length ? classes.mainHeaderCell : ""}
                  key={index}
                  style={{ height: "40px", fontWeight: 600, minWidth: header.minWidth || "" }}
                  colSpan={header.subHeaders?.length || 1}
                >
                  {header.subHeaders ? (
                    <Typography style={{ fontWeight: "bold" }}>{header.text}</Typography>
                  ) : (
                    <TableSortLabel
                      active={!!sortColumns[header.key]}
                      direction={sortColumns[header.key] || "asc"}
                      onClick={() => sortByColumn(header.key)}
                    >
                      <Typography style={{ fontWeight: "bold" }}>{header.text}</Typography>
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
            <TableRow className={classes.subHeadersRow}>
              {HEADERS.map((header, j) => {
                const subHeaders = header.subHeaders || [{}];
                return (
                  <React.Fragment key={j}>
                    {subHeaders.map((subHeader, i) => (
                      <TableCell
                        className={classes.subHeaderCell}
                        key={i}
                        style={{ minWidth: subHeader.minWidth || "" }}
                      >
                        {subHeader.key && (
                          <TableSortLabel
                            active={!!sortColumns[subHeader.key]}
                            direction={sortColumns[subHeader.key] || "asc"}
                            onClick={() => sortByColumn(subHeader.key, header.key)}
                          >
                            <Typography style={{ fontWeight: "bold" }}>{subHeader.text}</Typography>
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                innerRef={index === rows.length - 1 && !row.id ? newRowRef : null}
                className={classes.row}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (!disableTable) {
                    setRightClickEvent(event);
                    setClickedRow(index);
                  }
                }}
                key={index}
              >
                <TableCell width={1}>
                  <img
                    onClick={() => {
                      if (!disableTable) {
                        deleteRow(index);
                        if (row?.id) deleteBuilding(row.id);
                      }
                    }}
                    src={trashIcon}
                    alt="trash-icon"
                    style={{ paddingLeft: 2, paddingTop: 6 }}
                  />
                </TableCell>

                <ColumnsData
                  row={row}
                  rowIndex={index}
                  onCellDataChange={onCellDataChange}
                  headers={HEADERS.slice(1, 4)}
                />

                <TableCell width={80}>
                  <Select
                    options={COUNTRIES}
                    value={row.country || ""}
                    onChange={(v) => {
                      onCellDataChange(index, "country", v);
                    }}
                    displayEmpty={true}
                    placeholder="Select"
                  />
                </TableCell>

                <ColumnsData
                  row={row}
                  rowIndex={index}
                  onCellDataChange={onCellDataChange}
                  headers={HEADERS.slice(5, 8)}
                />

                <TableCell width={1}>
                  <Select
                    options={AREA_TYPES}
                    value={row.area_type?.toUpperCase() || ""}
                    onChange={(v) => onCellDataChange(index, "area_type", v)}
                    displayEmpty={true}
                    placeholder="Select"
                  />
                </TableCell>

                <TableCell width={1}>
                  <Select
                    options={BUILDING_SEGMENTS}
                    value={getSegment(row.segment) || ""}
                    onChange={(v) => onCellDataChange(index, "segment", v)}
                    displayEmpty={true}
                    placeholder="Select"
                  />
                </TableCell>

                <ColumnsData
                  row={row}
                  rowIndex={index}
                  onCellDataChange={onCellDataChange}
                  headers={HEADERS.slice(10, 12)}
                />

                <SubColumnsData
                  row={row}
                  rowIndex={index}
                  onCellDataChange={onCellDataChange}
                  headers={HEADERS.slice(12)}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContextMenu
        items={contextMenuItems}
        event={rightClickEvent}
        onClose={() => {
          setRightClickEvent(null);
          setClickedRow(null);
        }}
        clickedRow={clickedRow}
        copiedRow={copiedRow}
      />

      {!disableTable && (
        <Box className={classes.addRow}>
          <TextButton text="Add row" onClick={addNewRow} />
        </Box>
      )}
    </>
  );
};
